import React, { useState, useEffect } from 'react';
import { Box, Typography, Snackbar, Alert } from '@mui/material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useAuth } from '../components/AuthContext';
import { resendVerificationEmail } from '../services/api';
import FilledButton from '../components/FilledButton';
import OutlinedButton from '../components/OutlinedButton';

const RESEND_EXPIRY_KEY = 'verificationResendExpiry';

export default function VerifyEmailSentPage() {
  const location = useLocation();
  const email = location.state?.email || '';
  const { isAuthenticated } = useAuth();

  const [resendCooldown, setResendCooldown] = useState(0);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const expiry = parseInt(localStorage.getItem(RESEND_EXPIRY_KEY) || '0', 10);
    if (expiry) {
      const remaining = Math.ceil((expiry - Date.now()) / 1000);
      if (remaining > 0) setResendCooldown(remaining);
      else localStorage.removeItem(RESEND_EXPIRY_KEY);
    }
  }, []);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const id = setInterval(() => {
      setResendCooldown((s) => {
        const next = s > 0 ? s - 1 : 0;
        if (next === 0) localStorage.removeItem(RESEND_EXPIRY_KEY);
        return next;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [resendCooldown]);

  const handleResendVerification = async () => {
    try {
      const res = await resendVerificationEmail();
      if (res?.retry_after) {
        setResendCooldown(res.retry_after);
        localStorage.setItem(
          RESEND_EXPIRY_KEY,
          Date.now() + res.retry_after * 1000
        );
      }
      setSuccessMessage(
        'Verification email sent. Check your inbox or spam folder.'
      );
    } catch (err) {
      const retry = err.response?.data?.retry_after;
      if (retry) {
        setResendCooldown(retry);
        localStorage.setItem(
          RESEND_EXPIRY_KEY,
          Date.now() + retry * 1000
        );
      }
      setErrorMessage(
        err.response?.data?.detail || 'Could not resend verification email.'
      );
    }
  };

  return (
    <Box sx={{ textAlign: 'center', py: { xs: 6, md: 8 } }}>
      <Typography variant="h4" gutterBottom>
        Almost done!
      </Typography>
      <Typography sx={{ mb: 2 }}>
        We sent a verification link to <strong>{email}</strong>. Didn’t get it? You can resend the email or verify later from Settings.
      </Typography>
      <Box sx={{ mb: 2 }}>
        <OutlinedButton
          onClick={handleResendVerification}
          disabled={resendCooldown > 0}
          text={
            resendCooldown > 0
              ? `Email sent! (${resendCooldown}s)`
              : 'Resend verification email'
          }
          color="primary"
          sx={{ mr: 2 }}
        />
        <FilledButton
          component={RouterLink}
          to={isAuthenticated ? '/settings' : '/sign-in'}
          text={isAuthenticated ? 'Back to Settings' : 'Back to Sign In'}
          color="primary"
        />
      </Box>
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={4000}
        onClose={() => setErrorMessage('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setErrorMessage('')}
          severity="error"
          sx={{ width: '100%' }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSuccessMessage('')}
          severity="success"
          sx={{ width: '100%' }}
        >
          {successMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
